import {
  COPPEL_BANCO_ID,
  COPPEL_BANCO_NOMBRE,
  coppelIntegracionActiva,
} from './constants'
import { esIdBancoCoppel, seleccionIncluyeCoppel } from './bancos'
import {
  fetchCoppelAmortization,
  fetchCoppelPdfBlob,
  fetchCoppelPdfUrl,
  fetchCoppelSocTemplate,
  getAmortizationTableId,
  postCoppelProductApplication,
} from './api'
import { buildCoppelAnswers, labelEsquemaCoppel, listarCombinacionesCoppel, parseNumero } from './mappers'
import { setCoppelLogoInstitucion } from './display'
import { formatTasaPorcentajeDisplay } from './tasas'

function catAPorcentaje(cat) {
  const n = parseNumero(cat)
  if (n <= 1 && n > 0) return n * 100
  return n
}

function tasaAPorcentaje(tasa) {
  const n = parseNumero(tasa)
  if (n <= 1 && n > 0) return n * 100
  return n
}

function extraerMetricasAmortizacion(amortPayload) {
  const attrs = (amortPayload && amortPayload.data && amortPayload.data.attributes) || {}
  const info = attrs.additional_information || {}
  const totals = info.totals || attrs.totals || {}
  const primerPago = info.primer_pago || attrs.primer_pago || {}

  const mensualidadInicial =
    parseNumero(primerPago.pago_mensual) ||
    parseNumero(primerPago['Pago mensual']) ||
    parseNumero(info['Pago Mensual Total'])

  const mensualidadPromedio =
    parseNumero(totals.pago_mensual_total) ||
    parseNumero(info['Pago Mensual Total']) ||
    mensualidadInicial

  return {
    cat: attrs.cat,
    mensualidadInicial,
    mensualidadPromedio,
    pagoTotal: parseNumero(totals.pago_mensual_sin_iva),
    gastosNotariales: parseNumero(totals.gastos_notariales),
    comisionApertura: parseNumero(totals.comision_por_contratacion_credito),
    avaluo: parseNumero(totals.pre_originacion_avaluo_hipotecario),
  }
}

/** Métricas de comparador ya obtenidas de amortization_tables (o cotización legacy). */
export function itemTieneMetricasCoppelCompletas(item) {
  if (!item || !item.EsApiCoppel) return true
  if (item.CoppelMetricasCargadas === true) return true
  return parseNumero(item.Mensualidad) > 0
}

function aplicarMetricasAmortizacionAItem(item, amortPayload) {
  const m = extraerMetricasAmortizacion(amortPayload)
  item.MensualidadInicial = m.mensualidadInicial
  item.MensualidadPromedio = m.mensualidadPromedio
  item.MensualidadInicialPromedio = m.mensualidadInicial
  item.Mensualidad = m.mensualidadInicial
  item.Cat = catAPorcentaje(m.cat)
  item.GastosNotariales = m.gastosNotariales
  item.ComisionApertura = m.comisionApertura
  item.Avaluo = m.avaluo
  item.PagoTotal = m.pagoTotal
  item.GastosIniciales = m.avaluo + m.comisionApertura + m.gastosNotariales
  item.CoppelMetricasCargadas = true
  return item
}

async function obtenerAmortizacionCoppel(vm, item) {
  if (!item || !item.CoppelAmortizationId) return null
  if (item._coppelAmortPayload && item._coppelAmortPayload.data) {
    return item._coppelAmortPayload
  }
  const amortPayload = await fetchCoppelAmortization(vm.$axios, item.CoppelAmortizationId)
  if (amortPayload && amortPayload.data) {
    item._coppelAmortPayload = amortPayload
  }
  return amortPayload
}

function sincronizarItemCoppelEnLista(vm, item) {
  if (!vm || !vm.items || !item || !item.CoppelApplicationId) return
  const idx = vm.items.findIndex(
    (i) => esIdBancoCoppel(i.BancoId) && i.CoppelApplicationId === item.CoppelApplicationId
  )
  if (idx >= 0) {
    Object.assign(vm.items[idx], item)
  }
}

/**
 * GET amortization_tables solo si faltan métricas (favorito, seleccionar, detalle).
 * @returns {Promise<boolean>}
 */
export async function asegurarMetricasCoppelCargadas(vm, item) {
  if (!item || !item.EsApiCoppel) return true
  if (itemTieneMetricasCoppelCompletas(item)) return true
  if (!item.CoppelAmortizationId) return false

  const amortPayload = await obtenerAmortizacionCoppel(vm, item)
  if (!amortPayload || !amortPayload.data) return false

  aplicarMetricasAmortizacionAItem(item, amortPayload)
  sincronizarItemCoppelEnLista(vm, item)
  return true
}

/** Completa tarjetas en segundo plano tras cotizar (no bloquea el spinner de Cotizar). */
function precargarMetricasCoppelEnBackground(vm, items) {
  if (!vm || !items || !items.length) return

  items.forEach((item) => {
    if (itemTieneMetricasCoppelCompletas(item)) return
    asegurarMetricasCoppelCargadas(vm, item).catch((err) => {
      console.warn('Coppel precarga amortización', item.CoppelApplicationId, err)
    })
  })
}

function buildItemFromCoppelResponses(productPayload, amortPayload, meta) {
  const attrs = productPayload.data.attributes || {}
  const answers = attrs.answers || {}
  const m = extraerMetricasAmortizacion(amortPayload)

  const plazoMeses = parseInt(answers.plazo_en_meses || meta.plazoAnios * 12, 10)
  const valorInmueble = parseNumero(answers.valor_del_inmueble)
  const montoCredito = parseNumero(answers.monto_del_credito_solicitado)
  let aforo = parseNumero(answers.aforo)
  if ((!aforo || Number.isNaN(aforo)) && valorInmueble > 0) {
    aforo = (montoCredito / valorInmueble) * 100
  }
  if (aforo <= 1 && aforo > 0) aforo = aforo * 100

  const tasaDecimal = answers.tasa_de_interes != null ? answers.tasa_de_interes : meta.tasaDeInteres
  const esquemaLabel = labelEsquemaCoppel(meta.esquemaId)
  let nombreBase = attrs.product_name || `Coppel ${meta.plazoAnios} años`
  if (!String(nombreBase).toLowerCase().includes('creciente') && !String(nombreBase).toLowerCase().includes('fijo')) {
    nombreBase = `${nombreBase} (${esquemaLabel})`
  }
  const nombreConTasa =
    meta.variasTasas && tasaDecimal != null
      ? `${nombreBase} ${formatTasaPorcentajeDisplay(tasaDecimal)}`
      : nombreBase

  if (attrs.institution_logo) {
    setCoppelLogoInstitucion(attrs.institution_logo)
  }

  return {
    BancoId: COPPEL_BANCO_ID,
    Banco: COPPEL_BANCO_NOMBRE,
    Producto: nombreConTasa,
    Logo: attrs.institution_logo || '',
    LogoEsUrl: true,
    Esquema: parseInt(meta.esquemaId, 10),
    Esquemas: parseInt(meta.esquemaId, 10),
    Plazo: plazoMeses,
    Cat: catAPorcentaje(m.cat || attrs.cat),
    Tasa: tasaAPorcentaje(tasaDecimal != null ? tasaDecimal : answers.tasa_de_interes),
    IngresoMinimo: parseNumero(attrs.minimum_income_required),
    MensualidadInicial: m.mensualidadInicial,
    MensualidadPromedio: m.mensualidadPromedio,
    MensualidadInicialPromedio: m.mensualidadInicial,
    Mensualidad: m.mensualidadInicial,
    PagoTotal: m.pagoTotal,
    Monto: montoCredito || parseNumero(answers.monto_del_credito_solicitado),
    Enganche: parseNumero(answers.enganche),
    Aforo: aforo,
    Avaluo: m.avaluo,
    ComisionApertura: m.comisionApertura,
    GastosNotariales: m.gastosNotariales,
    GastosIniciales: m.avaluo + m.comisionApertura + m.gastosNotariales,
    vctPromociones: [],
    Selected: false,
    EsApiCoppel: true,
    CoppelApplicationId: productPayload.data.id,
    IdIon: productPayload.data.id,
    CoppelAmortizationId: getAmortizationTableId(productPayload),
    CoppelPdfUrl: null,
    CoppelMetricasCargadas: !!(amortPayload && amortPayload.data),
    Variante: null,
    Id: null,
  }
}

async function cotizarCombinacion(vm, combo) {
  const answers = buildCoppelAnswers(vm, combo)
  const productPayload = await postCoppelProductApplication(vm.$axios, answers)

  if (!productPayload || !productPayload.data) {
    if (productPayload && productPayload.errors) {
      console.warn('Coppel cotización rechazada', {
        esquema: answers.esquema_de_pago,
        plazo: combo.plazoAnios,
        errors: productPayload.errors,
      })
    }
    return null
  }

  const amortId = getAmortizationTableId(productPayload)
  if (!amortId) {
    return null
  }

  // Opción A: solo POST en cotización; GET amortization_tables bajo demanda o en background.
  const item = buildItemFromCoppelResponses(productPayload, null, combo)
  try {
    item.CoppelPdfUrl = await fetchCoppelPdfUrl(vm.$axios, productPayload.data.id)
  } catch (e) {
    item.CoppelPdfUrl = null
  }
  return item
}

/**
 * Agrega productos Coppel a `vm.items` tras cotización del motor SOC.
 * Usar en Credito.vue (completa) y NuevaCotizacion.vue (rápida).
 */
export async function enriquecerItemsConCoppel(vm, { bancos, esquemas, plazos }) {
  if (!coppelIntegracionActiva()) return 0
  if (!bancos || !seleccionIncluyeCoppel(bancos)) return 0
  if (vm.caso.PrecalificacionTipo !== 1) return 0

  const coppelItems = await cotizarCoppelProductos(vm, { bancos, esquemas, plazos })
  const sinCoppel = (vm.items || []).filter((i) => !esIdBancoCoppel(i.BancoId))
  vm.items = coppelItems.length ? [...sinCoppel, ...coppelItems] : sinCoppel
  return coppelItems.length
}

/**
 * Cotiza todas las combinaciones esquema × plazo para Coppel.
 * @param {Vue} vm - instancia con `caso`, `estados`, `destino` o `caso.destino`
 * @param {{ bancos: number[], esquemas: number[], plazos?: number[] }} params
 */
export async function cotizarCoppelProductos(vm, { bancos, esquemas, plazos }) {
  if (!coppelIntegracionActiva()) return []
  if (vm.caso.PrecalificacionTipo !== 1) return []
  if (!seleccionIncluyeCoppel(bancos)) return []

  vm.destino = vm.destino || (vm.caso && vm.caso.destino)
  const template = await fetchCoppelSocTemplate()
  const combinaciones = listarCombinacionesCoppel(vm, esquemas, plazos, template)
  if (!combinaciones.length) {
    console.warn('Coppel: sin combinaciones', {
      destino: vm.destino,
      esquemas,
      plazos: plazos || vm.caso.plazos,
      programa: vm.caso.programa,
    })
    return []
  }

  const items = []
  const variasTasas = new Set(combinaciones.map((c) => c.tasaDeInteres)).size > 1

  for (const combo of combinaciones) {
    try {
      const item = await cotizarCombinacion(vm, { ...combo, variasTasas })
      if (item) items.push(item)
    } catch (error) {
      console.error('Coppel cotización', combo, error)
    }
  }

  precargarMetricasCoppelEnBackground(vm, items)

  return items
}

export async function cargarDetalleAmortizacionCoppel(vm, item) {
  const ok = await asegurarMetricasCoppelCargadas(vm, item)
  if (!ok) {
    throw new Error('No se pudo cargar la amortización Coppel')
  }
  return item
}

/** Índices de columnas en `attributes.periods` (Abacus SOC). */
const PERIODO_IDX = {
  mes: 0,
  capital: 2,
  amortizacion: 4,
  interes: 5,
  mensualidad: 6,
  segVida: 7,
  segDanio: 8,
  pagoMensual: 9,
}

function mapPeriodosAbacusATabla(periods, tasa) {
  const tasaTabla = tasaAPorcentaje(tasa)
  return (periods || []).map((row) => {
    const v = Array.isArray(row) ? row : []
    return {
      Mes: parseNumero(v[PERIODO_IDX.mes]),
      Capital: parseNumero(v[PERIODO_IDX.capital]),
      Tasa: tasaTabla,
      Interes: parseNumero(v[PERIODO_IDX.interes]),
      Amortizacion: parseNumero(v[PERIODO_IDX.amortizacion]),
      Mensualidad: parseNumero(v[PERIODO_IDX.mensualidad]),
      SegVida: parseNumero(v[PERIODO_IDX.segVida]),
      SegDanio: parseNumero(v[PERIODO_IDX.segDanio]),
      SegDesempleo: 0,
      SegComision: 0,
      GastosDiferidos: 0,
      PagoMensual: parseNumero(v[PERIODO_IDX.pagoMensual]),
    }
  })
}

function acumularTotalesTablaAmortizacion(vm, itemsTable) {
  vm.interesesTotales = 0
  vm.mensualidadTotal = 0
  vm.amortizacionTotal = 0
  vm.gastosTotales = 0
  vm.pagoMensualTotal = 0
  vm.mesesTotales = 0
  vm.montoSolicitado = 0
  vm.tasa = 0
  vm.sumaSegVida = 0
  vm.sumaSegDanio = 0
  vm.sumaSegDesempleo = 0
  vm.sumaSegComision = 0

  if (!itemsTable.length) return

  vm.montoSolicitado = itemsTable[0].Capital

  for (let i = 0; i < itemsTable.length; i++) {
    const fila = itemsTable[i]
    if (fila.Mes !== 0) {
      vm.interesesTotales += fila.Interes
      vm.mensualidadTotal += fila.Mensualidad
      vm.amortizacionTotal += fila.Amortizacion
      vm.gastosTotales += fila.GastosDiferidos
      vm.pagoMensualTotal += fila.PagoMensual
      vm.sumaSegVida += fila.SegVida
      vm.sumaSegDanio += fila.SegDanio
      vm.sumaSegDesempleo += fila.SegDesempleo
      vm.sumaSegComision += fila.SegComision
    }
    vm.tasa = fila.Tasa
    vm.mesesTotales = fila.Mes
  }
}

/** Totales desde `additional_information.totals` (mismo criterio que filas del motor). */
function aplicarTotalesDesdeAbacus(vm, item, amortPayload, itemsTable) {
  const attrs = (amortPayload && amortPayload.data && amortPayload.data.attributes) || {}
  const totals = (attrs.additional_information || {}).totals || {}

  acumularTotalesTablaAmortizacion(vm, itemsTable)

  if (totals.intereses != null) {
    vm.interesesTotales = parseNumero(totals.intereses)
  }
  if (totals.amortizacion_a_capital != null) {
    vm.amortizacionTotal = parseNumero(totals.amortizacion_a_capital)
  } else if (totals.pago_del_credito != null) {
    vm.amortizacionTotal = parseNumero(totals.pago_del_credito)
  }
  if (totals.pago_del_credito != null) {
    vm.mensualidadTotal = parseNumero(totals.pago_del_credito)
  } else if (totals.pago_mensual != null) {
    vm.mensualidadTotal = parseNumero(totals.pago_mensual)
  }
  if (totals.pago_mensual_sin_iva != null) {
    vm.pagoMensualTotal = parseNumero(totals.pago_mensual_sin_iva)
  } else if (totals.pago_mensual != null) {
    vm.pagoMensualTotal = parseNumero(totals.pago_mensual)
  }
  if (totals.seguro_de_vida_y_desempleo != null) {
    vm.sumaSegVida = parseNumero(totals.seguro_de_vida_y_desempleo)
  }
  if (totals.seguro_de_danos != null) {
    vm.sumaSegDanio = parseNumero(totals.seguro_de_danos)
  }

  if (item.Monto != null && item.Monto !== '') {
    vm.montoSolicitado = parseNumero(item.Monto)
  } else if (itemsTable.length) {
    vm.montoSolicitado = itemsTable[0].Capital
  }

  if (item.Tasa != null && item.Tasa !== '') {
    vm.tasa = tasaAPorcentaje(item.Tasa)
  }
}

/**
 * Muestra la tabla de amortización en el popup del comparador (mismo patrón que Kredi/ION).
 * Evita GET …/pdf en el navegador: en sandbox Abacus suele responder 504 por timeout (~60s).
 */
export async function mostrarTablaAmortizacionCoppel(vm, item) {
  if (!item.CoppelAmortizationId) {
    return false
  }

  const amortPayload = await obtenerAmortizacionCoppel(vm, item)
  if (amortPayload && amortPayload.data) {
    aplicarMetricasAmortizacionAItem(item, amortPayload)
    sincronizarItemCoppelEnLista(vm, item)
  }
  const periods = (amortPayload && amortPayload.data && amortPayload.data.attributes && amortPayload.data.attributes.periods) || []

  if (!periods.length) {
    return false
  }

  const filas = mapPeriodosAbacusATabla(periods, item.Tasa)
  vm.itemsTable = filas
  aplicarTotalesDesdeAbacus(vm, item, amortPayload, filas)

  await vm.$nextTick()
  vm.popupActiveExc = true
  vm.popupActive = false
  return true
}

/**
 * Abre el PDF de amortización Coppel en pestaña nueva.
 * Solo considera éxito si Abacus devuelve bytes PDF válidos (blob).
 * No abrir la URL directa en el navegador: en sandbox Abacus suele responder 504 (~60s)
 * y eso impedía el fallback a tabla JSON en el comparador.
 */
export async function abrirPdfCoppel(vm, item) {
  const appId = item.CoppelApplicationId
  if (!appId) return false

  const blob = await fetchCoppelPdfBlob(vm.$axios, appId)
  if (!blob) return false

  const blobUrl = URL.createObjectURL(blob)
  const ventana = window.open(blobUrl, '_blank')
  if (ventana) {
    setTimeout(() => URL.revokeObjectURL(blobUrl), 120000)
    return true
  }
  URL.revokeObjectURL(blobUrl)
  return false
}

/**
 * Abre la tabla de amortización Coppel según R-ISO-81:
 * 1) Pestaña nueva con PDF (`GET …/product_applications/{id}/pdf`) — regla del acta.
 * 2) Si el PDF no responde (p. ej. 504 en sandbox), fallback: popup con datos de `GET /amortization_tables/{id}`.
 */
export async function abrirTablaAmortizacionCoppel(vm, item) {
  const pdfAbierto = await abrirPdfCoppel(vm, item)
  if (pdfAbierto) {
    return true
  }

  const tablaJson = await mostrarTablaAmortizacionCoppel(vm, item)
  if (tablaJson) {
    vm.$vs.notify({
      title: 'PDF no disponible',
      text: 'No se pudo obtener el PDF de Coppel. Se muestra la tabla en el comparador.',
      color: 'warning',
      position: 'top-right',
    })
    return true
  }

  return false
}
